import getMostSpecificAddressRegion from './get-most-specific-address';
import { getPositionFromDescription, getPoiPositionFromDescription } from './google-maps/getPositionFromDescription';
import { ProvinceOrStateCode } from './province_or_state';
import { provinceOrStateNameFromCode } from './province_or_state';
import { AddressType, SingleAddress } from '@zoocasa/go-search';
import { CountryCode, CountryCodeList } from 'types/countries';
import { MILITARY_BASE_ZOOM_LEVEL } from './determine-zoom-level';

import type Address from 'data/addresses';
import type { NextRouter } from 'next/router';
import type ListingParams from 'contexts/preferences/listing-params';
import type { ParsedMilitaryPath } from 'data/military/militaryPathParser';

/** @deprecated use {@link navigateToAddressMapView} or {@link navigateToMilitaryBaseMapView} instead */
export default async function clickMapView(listingParams: ListingParams, addresses: readonly Address[], router: NextRouter, useUsListings?: boolean) {
  const push = router.push.bind(router);
  const addressRegion = getMostSpecificAddressRegion(addresses);
  if (addressRegion?.addressType === 'province') {
    listingParams.setZoom(7);
  } else {
    listingParams.setZoom(14);
  }
  if (addressRegion) {
    const formattedAddressSearchQuery = `${addressRegion.neighbourhood}, ${addressRegion.subDivision}, ${provinceOrStateNameFromCode(addressRegion.province.toLowerCase() as ProvinceOrStateCode)}, ${addressRegion.country}`.replaceAll('null, ', '');
    const data = await getPositionFromDescription(formattedAddressSearchQuery, useUsListings).catch(() => {
      window.location = '/search' as unknown as Location;
      return;
    });
    if (data) {
      const { latitude, longitude } = data;
      listingParams.setLatitude(latitude);
      listingParams.setLongitude(longitude);
      listingParams.setAreaName(formattedAddressSearchQuery.split(',')[0]);
    }
  }
  push('/search');
}

/**
 * Common utility function to handle map navigation with coordinates
 */
async function navigateWithCoordinates(
  listingParams: ListingParams,
  formattedAddressSearchQuery: string,
  zoomLevel: number,
  isUSListing: boolean,
  push: NextRouter['push'],
  positionFn: (query: string, useUs: boolean) => ReturnType<typeof getPositionFromDescription> = getPositionFromDescription
) {
  listingParams.setZoom(zoomLevel);

  try {
    const data = await positionFn(formattedAddressSearchQuery, isUSListing);
    if (data) {
      const { latitude, longitude } = data;
      listingParams.setLatitude(latitude);
      listingParams.setLongitude(longitude);
    }
  } catch {
    window.location = '/search' as unknown as Location;
    return;
  }
  push('/search');
}

/**
 * An already-known map position (e.g. the current map center/zoom) that can be
 * passed to the navigation helpers to skip re-geocoding the area.
 */
export type MapViewPosition = {
  latitude: number;
  longitude: number;
  zoom: number;
};

/**
 * Sets an explicit, already-known map position and navigates to the map view,
 * skipping the geocode lookup. Used when the caller already knows exactly where
 * the map should land (e.g. preserving the current viewport when removing a
 * boundary) so the center/zoom don't jump to a geocoded point.
 */
function navigateWithKnownPosition(
  listingParams: ListingParams,
  position: MapViewPosition,
  push: NextRouter['push']
) {
  listingParams.setZoom(position.zoom);
  listingParams.setLatitude(position.latitude);
  listingParams.setLongitude(position.longitude);
  push('/search');
}

/**
 * Navigates to the map view for a regular address.
 *
 * When `position` is supplied, that exact center/zoom is used (preserving the
 * current viewport); otherwise the area is geocoded as before.
 */
export async function navigateToAddressMapView(
  listingParams: ListingParams,
  address: SingleAddress,
  push: NextRouter['push'],
  countryFilter: CountryCode,
  position?: MapViewPosition
) {
  if (position) {
    navigateWithKnownPosition(listingParams, position, push);
    return;
  }

  const formattedAddressSearchQuery = [
    address.neighbourhood,
    address.subDivision,
    address.provinceOrState,
    address.country,
  ].filter(Boolean).join(', ');

  const zoomLevel = address.addressType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE ? 7 : 13;

  await navigateWithCoordinates(
    listingParams,
    formattedAddressSearchQuery,
    zoomLevel,
    countryFilter === CountryCodeList.UNITED_STATES,
    push
  );
}

/**
 * Navigates to the map view for a military base.
 *
 * When `position` is supplied, that exact center/zoom is used (preserving the
 * current viewport); otherwise the base is geocoded as before.
 */
export async function navigateToMilitaryBaseMapView(
  listingParams: ListingParams,
  militaryBase: ParsedMilitaryPath,
  push: NextRouter['push'],
  countryFilter: CountryCode,
  position?: MapViewPosition
) {
  if (position) {
    navigateWithKnownPosition(listingParams, position, push);
    return;
  }

  await navigateWithCoordinates(
    listingParams,
    militaryBase.baseName,
    MILITARY_BASE_ZOOM_LEVEL,
    countryFilter === CountryCodeList.UNITED_STATES,
    push,
    getPoiPositionFromDescription
  );
}
